import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  Chip,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TicketTypeService from '../../../services/ticketType.service';
import ActivityService from '../../../services/activity.service';
import TicketService from '../../../services/ticket.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px', 
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px'
  },
  imagePreview: {
    width: '100%',
    maxWidth: '250px',
    height: '150px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  }
};

function Form({ open, onClose, initialData = null, onSubmitSuccess }) {
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    ticket_type_id: '',
    price: '',
    quantity: '',
    activities: [],
    related_tickets: [],
    image: '',
    template: ''
  });

  const [ticketTypes, setTicketTypes] = React.useState([]);
  const [activities, setActivities] = React.useState([]);
  const [tickets, setTickets] = React.useState([]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [ticketTypesResponse, activitiesResponse, ticketsResponse] = await Promise.all([
          TicketTypeService.list(),
          ActivityService.list(),
          TicketService.list()
        ]);
        setTicketTypes(ticketTypesResponse);
        setActivities(activitiesResponse);
        setTickets(ticketsResponse);
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
      }
    };
    fetchData();
  }, []);

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        ticket_type_id: initialData.ticket_type?.id || '',
        price: initialData.price || '',
        quantity: initialData.quantity || '',
        activities: initialData.activities?.map(a => a.id) || [],
        related_tickets: initialData.related_tickets?.map(t => t.id) || [],
        image: '',
        template: ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        ticket_type_id: '',
        price: '',
        quantity: '',
        activities: [],
        related_tickets: [],
        image: '',
        template: ''
      });
    }
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTemplateChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          template: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const dataToSubmit = {
        ...formData
      };

      if (!formData.image) delete dataToSubmit.image;
      if (!formData.template) delete dataToSubmit.template;

      if (initialData) {
        await TicketService.update({
          id: initialData.id,
          ...dataToSubmit
        });
      } else {
        await TicketService.create(dataToSubmit);
      }
      onSubmitSuccess(dataToSubmit);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier le ticket' : 'Nouveau ticket'}</span>
        <IconButton 
          onClick={handleClose}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          <Box sx={{ mb: 2 }}>
            {(formData.image || initialData?.image) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Image actuelle
                </Typography>
                <img 
                  src={formData.image || `${process.env.REACT_APP_BASE_URL}/${initialData.image}`}
                  alt="Aperçu du ticket"
                  style={styles.imagePreview}
                />
              </Box>
            )}
            <TextField
              fullWidth
              type="file"
              label="Image du ticket"
              onChange={handleImageChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            {(formData.template || initialData?.template) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Template actuel
                </Typography>
                <img 
                  src={formData.template || `${process.env.REACT_APP_BASE_URL}/${initialData.template}`}
                  alt="Template du ticket"
                  style={styles.imagePreview}
                />
              </Box>
            )}
            <TextField
              fullWidth
              type="file"
              label="Template du ticket"
              onChange={handleTemplateChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
          
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Prix"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleChange}
              required
              size="small"
            />

            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Quantité"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              required
              size="small"
            />
          </Box>

          <TextField
            fullWidth
            select
            label="Type de ticket"
            name="ticket_type_id"
            value={formData.ticket_type_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {ticketTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>

          <FormControl fullWidth size="small">
            <InputLabel>Activités incluses</InputLabel>
            <Select
              multiple
              name="activities"
              value={formData.activities}
              onChange={handleChange}
              input={<OutlinedInput label="Activités incluses" />}
              renderValue={(selected) => (
                <Box sx={styles.chips}>
                  {selected.map((value) => {
                    const activity = activities.find(a => a.id === value);
                    return (
                      <Chip 
                        key={value} 
                        label={activity?.name} 
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            >
              {activities.map((activity) => (
                <MenuItem key={activity.id} value={activity.id}>
                  {activity.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.activities.length > 1 && (
            <FormControl fullWidth size="small">
              <InputLabel>Tickets inclus</InputLabel>
              <Select
                multiple
                name="related_tickets"
                value={formData.related_tickets}
                onChange={handleChange}
                input={<OutlinedInput label="Tickets inclus" />}
                renderValue={(selected) => (
                  <Box sx={styles.chips}>
                    {selected.map((value) => {
                      const ticket = tickets.find(t => t.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={ticket?.name} 
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px'
                  }
                }}
              >
                {tickets.map((ticket) => (
                  <MenuItem key={ticket.id} value={ticket.id}>
                    {ticket.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button 
            onClick={handleClose}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    ticket_type: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activities: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })),
    related_tickets: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })),
    image: PropTypes.string,
    template: PropTypes.string
  })
};

export default Form;